---
id: apps-app-react-auth-site-doc
title: App React Auth Site
owner: Luc LEMEREZ
status: Active
lastReviewed: 2026-02-19
visibility: internal
tags:
  - app
  - auth
  - frontend
  - astro
---

## Product Overview

### Who it serves

This app serves Rezi users who are entering or recovering access to their
accounts through dedicated authentication routes outside the main `app-react`
SPA.

### Primary workflow

Users land on `/login` or `/signup` for email/password or social sign-in.
Password recovery starts on `/reset`, continues through OTP verification on
`/reset-password`, and returns users to login after a successful password
change. New-account email verification runs through `/confirm-email`.

In production routing, these auth pages are served through the Cloudflare
Worker `astro-auth`, which proxies root/auth paths to
`https://app-react-auth.netlify.app` before falling back to the main app
origin for non-auth routes.

The app also supports campaign-style entry points (`/invite`, `/r`) and a
resume-save onboarding variant (`/signup-resume`) that presents a resume
preview while prompting account creation.

### Business rules

Auth routes preserve redirect and UX context query params (`redirect`,
`redirectTo`, `next`, `returnTo`, `language`, `theme`) so users are returned to
the intended destination after authentication. OTP verification is mandatory for
email confirmation and password reset completion. If OTP context is missing,
users are redirected back to login.

For `/signup-resume`, anonymous resume access is token-gated: incoming
`anon_resume_token` values are verified first, then mapped to a short-lived
cookie and used to fetch preview data.

### Outcomes

The app delivers a focused, low-friction authentication experience that routes
users into the main product (`/dashboard/resumes` by default) once login,
signup, confirmation, or password recovery succeeds.

### Out of scope

This app does not own the authenticated dashboard product experience, resume
editing workflows, or backend auth/business logic enforcement.

## Technical Overview

### Architecture flow

`apps/app-react-auth-site/src/layouts/AuthLayout.astro` is the integration
center for client-side behavior: it renders the shared auth shell, loads
analytics/support tooling, initializes Firebase Auth, orchestrates OTP flows,
and calls auth APIs via `API_GATEWAY`.

Route pages in `apps/app-react-auth-site/src/pages` are static Astro views that
annotate forms and OTP containers with `data-*` attributes consumed by the
layout script. Shared UI primitives are provided by
`apps/app-react-auth-site/src/_components/auth`.

### Entry points

Build and runtime entrypoints are defined by Nx and Astro config:
`apps/app-react-auth-site/project.json`, `apps/app-react-auth-site/package.json`,
and `apps/app-react-auth-site/astro.config.mjs`. The app is built as a static
site with file-style routes and no trailing slash.

### Key modules and flows

Email/password login and signup are handled through Firebase Auth dynamic
imports in `apps/app-react-auth-site/src/layouts/AuthLayout.astro`, with social
providers (Google/Facebook) using popup-first and redirect fallback behavior.

OTP verification and password reset flows post to `v1/otp/send`, `v1/otp/check`,
`v1/auth/confirm-email`, and `v1/new_password` via a shared `postApi` helper,
including reCAPTCHA Enterprise execution and `X-Recaptcha` request headers.

The resume-preview signup path validates `anon_resume_token` through a Firebase
Cloud Function (`verify_anon_resume_token`), reads Firestore `anon-resumes`
documents, and hydrates a client-side preview template.

### Integrations and side effects

The layout initializes Intercom, GTM/GA, Amplitude, Bento, LinkedIn
Insight, Rewardful, ProfitWell, and Churnkey scripts at runtime. It also
persists lightweight email drafts in `sessionStorage` and stores temporary
anonymous resume identifiers in cookies for preview continuity.

At the edge, the `astro-auth` Cloudflare Worker performs request routing. It
proxies known auth pages (`/`, `/login`, `/signup`, `/confirm-email`,
`/forgot-password`, `/reset-password`, `/r/*`, `/invite/*`) and Astro assets
to Netlify (`app-react-auth.netlify.app`), and falls back to the main app
origin when a request is outside auth scope or an asset lookup resolves to an
HTML fallback response.

### Constraints and risks

Core auth behavior depends on runtime environment variables (Firebase public
config, `API_GATEWAY`, reCAPTCHA keys). Missing values disable or degrade flows.
Because auth and OTP behavior are client-orchestrated, regressions in
`AuthLayout.astro` can affect all auth routes simultaneously.

## Changelog

- 2026-02-19: Added Cloudflare `astro-auth` worker routing behavior and Netlify proxy details.
- 2026-02-19: Initial documentation for `apps/app-react-auth-site` root.
